import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Avatar,
  Divider,
} from "@mui/material";
import { capitalizeName, formatEmail } from "./utils";
import { ORGANIZATION_BANNER_ASPECT_RATIO } from "../../utils/imageUpload";

interface ProfileSummaryPanelProps {
  user: {
    userType?: string;
    schoolName?: string;
    schoolLogo?: string;
    orgId?: string;
    orgBanner?: string | null;
    email?: string;
    soid?: string;
    fullName?: string;
    displayName?: string;
    schoolId?: string;
  };
  canEdit: boolean;
  logoError: boolean;
  logoVersion: number;
  logoTimestamp: number;
  bannerVersion: number;
  bannerTimestamp: number;
  onLogoError: () => void;
  onEditLogo: () => void;
  onBannerFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ProfileSummaryPanel: React.FC<ProfileSummaryPanelProps> = ({
  user,
  canEdit,
  logoError,
  logoVersion,
  logoTimestamp,
  bannerVersion,
  bannerTimestamp,
  onLogoError,
  onEditLogo,
  onBannerFileSelect,
}) => {
  const logoSrc =
    logoError || !user.schoolLogo
      ? "/placeholder.jpg"
      : logoVersion > 0 && logoTimestamp > 0
      ? `${user.schoolLogo}${user.schoolLogo?.includes("?") ? "&" : "?"}v=${logoVersion}&_t=${logoTimestamp}`
      : user.schoolLogo;

  const bannerSrc =
    bannerVersion > 0 && bannerTimestamp > 0 && user.orgBanner
      ? `${String(user.orgBanner)}${String(user.orgBanner).includes("?") ? "&" : "?"}v=${bannerVersion}&_t=${bannerTimestamp}`
      : String(user.orgBanner || "");

  const displayName =
    user.userType === "team"
      ? capitalizeName(user.fullName || user.displayName || "")
      : user.schoolName;

  const displayEmail = (() => {
    if (user.userType === "team" && user.email) {
      const [username] = user.email.split("@");
      return user.soid
        ? `${username}@${user.soid}`
        : formatEmail(user.email, user.orgId, user.soid);
    }
    return user.email;
  })();

  return (
    <Box sx={{ order: { xs: 1, md: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "none",
          height: "100%",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          color="text.primary"
          mb={3}
        >
          Your Organization Summary
        </Typography>
        <Stack spacing={3} alignItems="center">
          {/* Logo */}
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Avatar
              src={logoSrc}
              alt="School Logo"
              sx={{
                width: 120,
                height: 120,
                border: 3,
                borderColor: "primary.main",
                borderStyle: "solid",
                bgcolor: "background.paper",
              }}
              imgProps={{ onError: onLogoError }}
              key={`logo-${logoVersion}`}
            />
            {canEdit && (
              <Button
                onClick={onEditLogo}
                size="small"
                sx={{
                  position: "absolute",
                  bottom: -8,
                  right: -8,
                  bgcolor: "primary.main",
                  color: "common.white",
                  fontSize: "0.75rem",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  minWidth: 0,
                  zIndex: 1,
                  textTransform: "none",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                Edit
              </Button>
            )}
          </Box>

          {/* Account Name */}
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography
              variant="body1"
              fontWeight={600}
              color="text.primary"
              mb={1}
            >
              {displayName}
            </Typography>
          </Box>

          {/* Email */}
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              {canEdit && (
                <Button
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    minWidth: 0,
                    p: 0.5,
                  }}
                >
                  Edit
                </Button>
              )}
            </Box>
            <Typography variant="body2" fontWeight={500}>
              {displayEmail}
            </Typography>
          </Box>

          <Divider sx={{ width: "100%" }} />

          {/* Banner Image */}
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Banner Image
              </Typography>
              {canEdit && (
                <>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="banner-upload-input"
                    type="file"
                    onChange={onBannerFileSelect}
                  />
                  <label htmlFor="banner-upload-input">
                    <Button
                      component="span"
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        minWidth: 0,
                        p: 0.5,
                      }}
                    >
                      {user.orgBanner ? "Change" : "Upload"}
                    </Button>
                  </label>
                </>
              )}
            </Box>
            {user.orgBanner ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: ORGANIZATION_BANNER_ASPECT_RATIO,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 1,
                }}
              >
                <Box
                  component="img"
                  src={bannerSrc}
                  alt="Organization Banner"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: ORGANIZATION_BANNER_ASPECT_RATIO,
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "action.hover",
                  mb: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No banner image set
                </Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem" }}
            >
              Used as background on featured cards in the mobile app
            </Typography>
          </Box>

          <Divider sx={{ width: "100%" }} />

          {/* Profile ID */}
          {user.soid && (
            <Box sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary" mb={0.5}>
                Profile ID
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                @{user.soid}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default ProfileSummaryPanel;
